import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { LivePlayer, TeamSide } from '../liveMatchTypes.ts';

interface Props {
  open: boolean;
  side: TeamSide;
  outPlayerId: number;
  onClose: () => void;
}

export default function SubstitutionDialog({ open, side, outPlayerId, onClose }: Props) {
  const { state, dispatch } = useLiveMatch();

  const lineup     = side === 'HOME' ? state.homeLineup     : state.awayLineup;
  const callup     = side === 'HOME' ? state.homeCallup     : state.awayCallup;
  const bench      = side === 'HOME' ? state.homeBench      : state.awayBench;
  const shirtColor = side === 'HOME' ? state.homeShirtColor : state.awayShirtColor;

  const outFieldPos = lineup.find((fp) => fp.playerId === outPlayerId);
  const outPlayer   = callup.find((p) => p.id === outPlayerId);
  const benchPlayers: LivePlayer[] = bench
    .map((id) => callup.find((p) => p.id === id))
    .filter((p): p is LivePlayer => p !== undefined);

  function handleSelect(inPlayerId: number) {
    dispatch({ type: 'SUBSTITUTION', team: side, outPlayerId, inPlayerId });
    onClose();
  }

  if (!outPlayer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Sostituzione
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Giocatore in uscita */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mb: 2,
            borderRadius: 1,
            bgcolor: `${shirtColor}18`,
            border: '1px solid',
            borderColor: `${shirtColor}55`,
          }}
        >
          <Avatar sx={{ bgcolor: shirtColor, color: '#fff', fontWeight: 700 }}>
            {outPlayer.shirtNumber || '?'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2">
              {outPlayer.firstName} {outPlayer.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              #{outPlayer.shirtNumber} · pos {outFieldPos?.position} · {outFieldPos?.role ?? '—'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            esce
          </Typography>
        </Box>

        {/* Panchina */}
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          Scegli chi entra:
        </Typography>

        {benchPlayers.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            Nessun giocatore disponibile in panchina.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {benchPlayers.map((p) => (
              <Box
                key={p.id}
                onClick={() => handleSelect(p.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar sx={{ bgcolor: shirtColor, color: '#fff', fontWeight: 700, width: 36, height: 36, fontSize: '0.9rem' }}>
                  {p.shirtNumber || '?'}
                </Avatar>
                <Box>
                  <Typography variant="body2">{p.firstName} {p.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">#{p.shirtNumber}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        <Button onClick={onClose} fullWidth sx={{ mt: 2 }} color="inherit">
          Annulla
        </Button>
      </DialogContent>
    </Dialog>
  );
}
